import axiosInstance from 'shared/services/api/axiosInstance';

/**
 * Dashboards API — /api/dashboards. Every call names its team; the server
 * never falls back to the user's default team.
 */
const p = (team, extra = {}) => ({ params: { teamId: team, ...extra } });
const data = (r) => r.data;
// A write changes what the list shows (name, members, card count, public link).
const written = (r) => { listCache.clear(); return r.data; };

/**
 * The list is kept for a few minutes (the Reports list and the Overview both
 * read it) and dropped by any write. A dashboard read shares one request while
 * it is in flight (development double-mount); a read after a write is fresh.
 */
const LIST_TTL_MS = 3 * 60 * 1000;
const listCache = new Map(); // team -> { at, promise }
const getInflight = new Map();
export const invalidateDashboards = () => { listCache.clear(); };
// Ask changed a dashboard from somewhere this module did not see.
if (typeof window !== 'undefined') window.addEventListener('trodo:dashboard', invalidateDashboards);

export const listDashboards = (team) => {
  const hit = listCache.get(team);
  if (hit && Date.now() - hit.at < LIST_TTL_MS) return hit.promise;
  const promise = axiosInstance.get('/dashboards', p(team)).then((r) => r.data.dashboards);
  listCache.set(team, { at: Date.now(), promise });
  promise.catch(() => { if (listCache.get(team)?.promise === promise) listCache.delete(team); });
  return promise;
};
export const getDashboard = (team, id) => {
  const key = `${team}|${id}`;
  if (getInflight.has(key)) return getInflight.get(key);
  const promise = axiosInstance.get(`/dashboards/${id}`, p(team)).then(data).finally(() => getInflight.delete(key));
  getInflight.set(key, promise);
  return promise;
};
export const createDashboard = (team, body) => axiosInstance.post('/dashboards', body, p(team)).then(written);
export const updateDashboard = (team, id, body) => axiosInstance.patch(`/dashboards/${id}`, body, p(team)).then(written);
export const deleteDashboard = (team, id) => axiosInstance.delete(`/dashboards/${id}`, p(team)).then(written);
export const duplicateDashboard = (team, id) => axiosInstance.post(`/dashboards/${id}/duplicate`, {}, p(team)).then(written);
export const resetDashboard = (team, id) => axiosInstance.post(`/dashboards/${id}/reset`, {}, p(team)).then(written);
export const setEditors = (team, id, userIds) => axiosInstance.put(`/dashboards/${id}/editors`, { user_ids: userIds }, p(team)).then(written);

export const addTab = (team, id, name) => axiosInstance.post(`/dashboards/${id}/tabs`, { name }, p(team)).then(data);
export const renameTab = (team, id, tabId, name) => axiosInstance.patch(`/dashboards/${id}/tabs/${tabId}`, { name }, p(team)).then(data);
export const deleteTab = (team, id, tabId) => axiosInstance.delete(`/dashboards/${id}/tabs/${tabId}`, p(team)).then(data);

export const addCard = (team, id, card) => axiosInstance.post(`/dashboards/${id}/cards`, card, p(team)).then(written);
export const updateCard = (team, id, cardId, body) => axiosInstance.patch(`/dashboards/${id}/cards/${cardId}`, body, p(team)).then(data);
export const deleteCard = (team, id, cardId) => axiosInstance.delete(`/dashboards/${id}/cards/${cardId}`, p(team)).then(written);
export const saveLayout = (team, id, cards) => axiosInstance.put(`/dashboards/${id}/layout`, { cards }, p(team)).then(data);
export const runCard = (team, id, cardId, window, filters) => axiosInstance.post(`/dashboards/${id}/cards/${cardId}/run`, { window, filters }, p(team)).then(data);

export const shareDashboard = (team, id) => axiosInstance.post(`/dashboards/${id}/share`, {}, p(team)).then(written);
export const unshareDashboard = (team, id) => axiosInstance.delete(`/dashboards/${id}/share`, p(team)).then(written);

export const getFields = () => axiosInstance.get('/dashboards/fields').then((r) => r.data.entities);
export const getPresets = () => axiosInstance.get('/dashboards/presets').then((r) => r.data.presets);
export const previewCard = (team, card, window, filters) => axiosInstance.post('/dashboards/preview', { card, window, filters }, p(team)).then(data);
export const previewQuery = (team, query, window, filters) => axiosInstance.post('/dashboards/query', { query, window, filters }, p(team)).then(data);

export const getPublicDashboard = (token) => axiosInstance.get(`/public/dashboards/${encodeURIComponent(token)}`).then(data);
export const runPublicCard = (token, cardId, window) => axiosInstance.post(`/public/dashboards/${encodeURIComponent(token)}/cards/${cardId}/run`, { window }).then(data);
